import { sendRequest } from "@infrastructure/api/api";
import type ApiResponse from "@core/types/apiResponse";
import type { ErrorResponse } from "@core/types/errorResponse";
import type { DepartmentReportMediaItem, DepartmentReportDocument } from "./types";

/**
 * Pastor (Minister's Report) endpoints — structured, PDF-shaped data.
 *   GET /{scope}/{id}/department/report/pastor?reportTagId=
 *
 * Church → a single Minister's Report (status/finances/statistics/assessment).
 * District → churches[] + full-population totals.
 * Nation → districts[] (each with churches[] + per-district totals) + grand totals.
 *
 * Every value leaf and every totals entry carries a stable `field`
 * (activity_slug_kpi_slug) — the frontend joins church values to aggregated columns
 * by `field`, never by the (shared) kpi_id.
 */

export type PastorLeaf = {
  field: string;
  label: string;
  kpi_id: number;
  kpi_name: string | null;
  data_type: string | null;
  unit: string | null;
  value: string | number | boolean | null;
};

/** A statistics row may have times_held and/or number_of_hours; other sections are flat maps. */
export type PastorSection = Record<string, PastorLeaf | Record<string, PastorLeaf>>;

export type PastorTotals = Record<string, PastorLeaf>;

export type PastorDepartmentSubmission = {
  department_report_id: number;
  report_status_id: number;
  report_status_name: string;
  scope: string;
  notes: string | null;
  submitted_at: string | null;
  submitted_by: number | null;
  submitted_by_name?: string | null;
  approved_at?: string | null;
  approved_by?: number | null;
  approved_by_name?: string | null;
  approval_notes?: string | null;
  created_at: string | null;
  created_by: number | null;
  updated_at: string | null;
  updated_by: number | null;
} | null;

export type PastorChurchEntry = {
  church_id: number;
  church_name: string;
  media: DepartmentReportMediaItem[];
  department_submission: PastorDepartmentSubmission;
  status: PastorSection;
  finances: PastorSection;
  statistics: PastorSection;
  assessment: PastorSection;
};

export type LeadershipSourceTemplate = {
  template_id: number;
  template_name: string;
  submitted_by?: number | null;
};

export type PastorMeta = {
  church_id?: number;
  church_name?: string;
  district_id?: number;
  district_name?: string;
  nation_id?: number;
  nation_name?: string;
  department_id: number;
  department_name: string;
  report_template_id: number;
  report_template_name: string;
  /** Templates that feed this leadership report (for activity-submission review). */
  source_templates?: LeadershipSourceTemplate[];
  report_tag_id: number;
  report_tag_name: string;
  report_tag_since: string | null;
  report_tag_until: string | null;
  report_status_id?: number | null;
  report_status_name?: string | null;
  process_results?: unknown;
  department_submission?: PastorDepartmentSubmission;
  media: DepartmentReportMediaItem[] | null;
  documents?: DepartmentReportDocument[];
};

export type PastorChurchReport = {
  meta: PastorMeta;
  status: PastorSection;
  finances: PastorSection;
  statistics: PastorSection;
  assessment: PastorSection;
};

export type PastorDistrictReport = {
  meta: PastorMeta;
  churches: PastorChurchEntry[];
  totals: PastorTotals;
};

export type PastorNationDistrictEntry = {
  district_id: number;
  district_name: string;
  media: DepartmentReportMediaItem[];
  totals: PastorTotals;
  department_submission?: PastorDepartmentSubmission | null;
  churches: PastorChurchEntry[];
};

export type PastorNationReport = {
  meta: PastorMeta;
  districts: PastorNationDistrictEntry[];
  totals: PastorTotals;
};

export type PastorReportData =
  | PastorChurchReport
  | PastorDistrictReport
  | PastorNationReport;

/** The API wraps the structured object in a Laravel paginator envelope (`data`). */
export type PaginatedPastorResponse<T extends PastorReportData = PastorReportData> = {
  current_page: number;
  data: T;
  last_page: number;
  per_page: number;
  total: number;
  next_page_url?: string | null;
  prev_page_url?: string | null;
};

export type FetchPastorArgs =
  | { scope: "church"; churchId: number; reportTagId: number; page?: number; perPage?: number }
  | { scope: "district"; districtId: number; reportTagId: number; page?: number; perPage?: number }
  | { scope: "nation"; nationId: number; reportTagId: number; page?: number; perPage?: number };

function buildPath(args: FetchPastorArgs): string {
  if (args.scope === "church") return `/church/${args.churchId}/department/report/pastor`;
  if (args.scope === "district") return `/district/${args.districtId}/department/report/pastor`;
  return `/nation/${args.nationId}/department/report/pastor`;
}

export async function fetchPastorReportAsync<T extends PastorReportData = PastorReportData>(
  args: FetchPastorArgs
): Promise<ApiResponse<PaginatedPastorResponse<T>, ErrorResponse>> {
  const params = new URLSearchParams({ reportTagId: String(args.reportTagId) });
  if (args.page) params.set("page", String(args.page));
  if (args.perPage) params.set("perPage", String(args.perPage));

  const url = `${buildPath(args)}?${params.toString()}`;
  return sendRequest<any, PaginatedPastorResponse<T>>("GET", url);
}
